/**
 * API Admin avec gestion robuste des erreurs (P0.1)
 * - Timeouts configurables
 * - Retry automatique
 * - Gestion des erreurs avec messages explicites
 */

import { extractErrorMessage } from './errorMessage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_TIMEOUT = 15000; // 15 secondes

/**
 * Effectue un appel API avec timeout et gestion d'erreurs
 */
export async function apiCall(endpoint, options = {}) {
  const {
    method = 'GET',
    body = null,
    timeout = DEFAULT_TIMEOUT,
    retries = 1
  } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const fetchOptions = {
    method,
    headers: { 'Content-Type': 'application/json' },
    signal: controller.signal
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  let lastError = null;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${BACKEND_URL}${endpoint}`, fetchOptions);
      clearTimeout(timeoutId);

      // Parsing défensif : vérifier Content-Type avant JSON.parse
      const contentType = response.headers.get('content-type') || '';
      let data = null;
      
      if (contentType.includes('application/json')) {
        try {
          const text = await response.text();
          data = JSON.parse(text);
        } catch (jsonError) {
          // Si JSON.parse échoue malgré Content-Type JSON, traiter comme erreur
          lastError = {
            error_code: "non_json_response",
            message: `Réponse invalide du serveur (JSON attendu): ${jsonError.message}`,
            details: null
          };
          throw new Error(lastError.message);
        }
      } else {
        // Si ce n'est pas du JSON, lire le texte et construire une erreur structurée
        const text = await response.text();
        lastError = {
          error_code: "non_json_response",
          message: `Réponse non-JSON du serveur (Content-Type: ${contentType || 'non spécifié'}): ${text.slice(0, 200)}...`,
          details: text
        };
        throw new Error(lastError.message);
      }

      if (!response.ok) {
        // Construire une erreur structurée depuis la réponse
        // Toujours une chaîne : `detail` peut être un objet ou un tableau (422).
        const errorMessage = extractErrorMessage(
          data.message ?? data.detail,
          `Erreur ${response.status}`
        );
        lastError = {
          error_code: data.error_code || data.error || "http_error",
          message: errorMessage,
          details: data
        };
        throw new Error(errorMessage);
      }

      return { success: true, data };
    } catch (error) {
      // Si lastError a déjà été défini (non-JSON ou erreur structurée), l'utiliser
      if (!lastError || (error.name !== 'AbortError' && !lastError.error_code)) {
        if (error.name === 'AbortError') {
          lastError = {
            error_code: "timeout",
            message: 'La requête a expiré. Vérifiez votre connexion et réessayez.',
            details: null
          };
        } else {
          lastError = {
            error_code: "network_error",
            message: error.message || 'Erreur inconnue lors de la requête.',
            details: null
          };
        }
      }
      
      // Si on a encore des retries, attendre un peu avant de réessayer
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }

  clearTimeout(timeoutId);
  
  // Retourner une erreur structurée
  return { 
    success: false, 
    error: lastError?.message || 'Erreur inconnue',
    error_details: lastError || { error_code: "unknown_error", message: 'Erreur inconnue' }
  };
}

/**
 * Récupère le schéma d'un générateur (essaie le nouveau endpoint Factory, puis legacy)
 */
export async function fetchGeneratorSchema(generatorKey) {
  // Essayer d'abord le nouveau endpoint Factory
  const factoryResult = await apiCall(`/api/v1/exercises/generators/${generatorKey}/full-schema`);
  if (factoryResult.success) {
    // Adapter la réponse Factory au format legacy pour compatibilité
    const data = factoryResult.data;
    const presetParams = data.presets?.[0]?.params || {};
    const templateExampleEnonce =
      presetParams.enonce_template ||
      presetParams.enonce_template_html ||
      presetParams.enoncetemplate ||
      data.template_example_enonce ||
      '';
    const templateExampleSolution =
      presetParams.solution_template ||
      presetParams.solution_template_html ||
      presetParams.solutiontemplate ||
      data.template_example_solution ||
      '';
    const variables = (data.schema || []).map((param) => ({
      name: param.name || param.key || param.id || param.param || '',
      type: param.type || param.param_type || 'string',
      description: param.description || param.label || '',
      example: param.example ?? param.default ?? ''
    })).filter(v => v.name);
    return {
      success: true,
      data: {
        generator_key: data.generator_key || data.generatorkey,
        label: data.meta?.label || data.generator_key || data.generatorkey,
        description: data.meta?.description || '',
        niveau: data.meta?.niveaux?.[0] || '6e',
        variables,
        svg_modes: data.meta?.svg_mode ? [data.meta.svg_mode] : ['AUTO', 'CUSTOM'],
        supports_double_svg: data.meta?.supports_double_svg ?? true,
        pedagogical_tips: data.meta?.pedagogical_tips,
        exercise_type: data.meta?.exercise_type || data.exercise_type,
        template_example_enonce: templateExampleEnonce,
        template_example_solution: templateExampleSolution,
        presets: data.presets || [],
        defaults: data.defaults || {}
      }
    };
  }
  
  // Fallback sur l'ancien endpoint
  return apiCall(`/api/v1/exercises/generators/${generatorKey}/schema`);
}

/**
 * Liste tous les générateurs
 */
export async function fetchGeneratorsList() {
  return apiCall('/api/v1/exercises/generators/list');
}

/**
 * Preview d'un exercice dynamique
 */
export async function previewDynamicExercise(data) {
  return apiCall('/api/admin/exercises/preview-dynamic', {
    method: 'POST',
    body: data,
    timeout: 20000 // Preview peut être plus long
  });
}

/**
 * Crée un exercice pour un chapitre donné (CRUD admin)
 */
export async function createChapterExercise(chapterCode, exercisePayload) {
  return apiCall(`/api/admin/chapters/${chapterCode}/exercises`, {
    method: 'POST',
    body: exercisePayload,
    timeout: DEFAULT_TIMEOUT,
  });
}

/**
 * Met à jour un exercice existant (CRUD admin)
 */
export async function updateChapterExercise(chapterCode, exerciseId, exercisePayload) {
  return apiCall(`/api/admin/chapters/${chapterCode}/exercises/${exerciseId}`, {
    method: 'PUT',
    body: exercisePayload,
    timeout: DEFAULT_TIMEOUT,
  });
}

/**
 * Supprime un exercice existant (CRUD admin)
 */
export async function deleteChapterExercise(chapterCode, exerciseId) {
  return apiCall(`/api/admin/chapters/${chapterCode}/exercises/${exerciseId}`, {
    method: 'DELETE',
    timeout: DEFAULT_TIMEOUT,
  });
}

/**
 * Valide un template
 */
export async function validateTemplate(template, generatorKey) {
  return apiCall(`/api/admin/exercises/validate-template?template=${encodeURIComponent(template)}&generator_key=${generatorKey}`, {
    method: 'POST'
  });
}

/**
 * === LEVELS API (Niveaux scolaires) ===
 */

/**
 * Récupère tous les niveaux scolaires
 */
export async function fetchLevels() {
  return apiCall('/api/v1/admin/levels');
}

/**
 * Crée un nouveau niveau
 */
export async function createLevel(levelData) {
  return apiCall('/api/v1/admin/levels', {
    method: 'POST',
    body: levelData,
  });
}

/**
 * Met à jour un niveau existant
 */
export async function updateLevel(code, levelData) {
  return apiCall(`/api/v1/admin/levels/${code}`, {
    method: 'PUT',
    body: levelData,
  });
}

/**
 * Supprime un niveau
 */
export async function deleteLevel(code, force = false) {
  return apiCall(`/api/v1/admin/levels/${code}?force=${force}`, {
    method: 'DELETE',
  });
}

/**
 * Récupère les stats d'un niveau
 */
export async function fetchLevelStats(code) {
  return apiCall(`/api/v1/admin/levels/${code}?include_stats=true`);
}

/**
 * === BULK CREATE API (Exercices dynamiques manquants) ===
 */

/**
 * Récupère la matrice des exercices manquants (générateur x difficulté)
 */
export async function fetchMissingExercises(chapterCode) {
  return apiCall(`/api/admin/chapters/${chapterCode}/exercises/missing`);
}

/**
 * Crée en lot les exercices dynamiques sélectionnés
 */
export async function bulkCreateExercises(chapterCode, exercises) {
  return apiCall(`/api/admin/chapters/${chapterCode}/exercises/bulk-create`, {
    method: 'POST',
    body: { exercises },
    timeout: 60000, // 60s - peut être long si beaucoup d'exercices
  });
}

/**
 * === BUG REPORTS API ===
 */

/**
 * Signaler un problème — route UTILISATEUR, pas la route d'administration.
 *
 * `/api/admin/bug-reports` exige le statut d'administrateur. Tant que le
 * bouton n'était visible que pour un administrateur, cela ne se voyait pas ;
 * dès qu'on ouvre le signalement à une capacité de compte, poster sur la route
 * admin renvoie 403. `/api/retours` porte sa propre garde, fondée sur la
 * capacité `signaler`.
 */
export async function createBugReport(data) {
  return apiCall('/api/retours', {
    method: 'POST',
    body: data,
  });
}

export async function fetchBugReports(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const qs = params.toString();
  return apiCall(`/api/admin/bug-reports${qs ? `?${qs}` : ''}`);
}

export async function fetchBugReport(ticketId) {
  return apiCall(`/api/admin/bug-reports/${ticketId}`);
}

export async function updateBugReport(ticketId, data) {
  return apiCall(`/api/admin/bug-reports/${ticketId}`, {
    method: 'PUT',
    body: data,
  });
}

export async function fetchBugReportStats() {
  return apiCall('/api/admin/bug-reports/stats');
}

/** Le parcours des états, servi par le serveur plutôt que recopié ici. */
export async function fetchRetoursEtats() {
  return apiCall('/api/admin/bug-reports/etats');
}

/**
 * Rejoue la graine du ticket.
 *
 * `intention` ne change PAS le calcul — on ne sait pas exécuter l'ancienne
 * version d'un générateur. Elle change la question posée au résultat, et donc
 * la lecture de `identique`.
 */
export async function rejouerRetour(ticketId, intention = 'reproduire') {
  return apiCall(`/api/admin/bug-reports/${ticketId}/rejeu?intention=${intention}`, {
    method: 'POST',
    timeout: 60000,
  });
}

/** Le dossier autonome, copiable, qu'un agent peut lire seul. */
export async function fetchDossierRetour(ticketId) {
  return apiCall(`/api/admin/bug-reports/${ticketId}/dossier`);
}

/** Les capacités de compte et qui les possède. */
export async function fetchCapacites() {
  return apiCall('/api/admin/capacites');
}

export async function accorderCapacite(email, capacite) {
  return apiCall('/api/admin/capacites', { method: 'POST', body: { email, capacite } });
}

export async function retirerCapacite(email, capacite) {
  return apiCall(
    `/api/admin/capacites?email=${encodeURIComponent(email)}&capacite=${encodeURIComponent(capacite)}`,
    { method: 'DELETE' }
  );
}

export default {
  apiCall,
  fetchGeneratorSchema,
  fetchGeneratorsList,
  previewDynamicExercise,
  validateTemplate,
  createChapterExercise,
  updateChapterExercise,
  deleteChapterExercise,
  // Levels API
  fetchLevels,
  createLevel,
  updateLevel,
  deleteLevel,
  fetchLevelStats,
  // Bulk create API
  fetchMissingExercises,
  bulkCreateExercises,
  // Bug Reports API
  createBugReport,
  fetchBugReports,
  fetchBugReport,
  updateBugReport,
  fetchBugReportStats,
  fetchRetoursEtats,
  rejouerRetour,
  fetchDossierRetour,
  fetchCapacites,
  accorderCapacite,
  retirerCapacite,
};


/**
 * Le même appel, mais qui rend la charge utile et lève en cas d'échec.
 *
 * `apiCall` rend `{ success, data }` et ne lève JAMAIS : c'est un contrat sûr,
 * mais il se trahit à l'usage. Écrire `setDonnees(await apiCall(...))` paraît
 * juste, compile, ne lève rien — et pose `{success, data}` là où le composant
 * attend la charge utile. L'écran affiche alors un état vide, sans erreur.
 *
 * Mesuré : l'aperçu d'un générateur annonçait « cette série ne produit aucun
 * exercice » alors que le serveur en renvoyait quatre.
 *
 * `demander` est donc l'inverse assumé : elle rend les données, et une erreur
 * se voit parce qu'elle est levée. On garde les deux — l'ancienne pour le code
 * qui teste `success`, celle-ci pour le code qui préfère `try/catch`.
 */
export async function demander(endpoint, options = {}) {
  const reponse = await apiCall(endpoint, options);
  if (!reponse.success) {
    throw new Error(reponse.error || "La requête n'a pas abouti.");
  }
  return reponse.data;
}
